import classNames from 'classnames';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { matchPath, useLocation, useNavigate } from 'react-router';
import type { NavbarItemType } from 'types/navbar.types';
import {
  SidebarNavigationItemBGStyles,
  SidebarNavigationItemDefaultTextStyles,
  SideBarNavigationItemIconStyles,
  SidebarNavigationItemSelectedBGStyles,
  SidebarNavigationItemTextStyles,
} from './types';
import { Tooltip } from 'components/atoms/Tooltip';
import { logAmplitude } from 'utils/amplitude';
import useSubscriptionPlan from 'hooks/Subscriptions/useSubscriptionPlan';
import { isWeb } from 'utils/constants';
import { LockClosedIcon } from '@heroicons/react/outline';
import { useShouldShowTaxAdvisor } from 'hooks/TaxAdvisor/useShouldShowTaxAdvisor';

const NavigationItem: FC<{
  item: NavbarItemType;
  className?: string;
  active?: boolean;
  titleContainerClassName?: string;
}> = ({ item, active, titleContainerClassName = '', className = '' }) => {
  const { renderTitle, to, onClick, Icon, type, children, tracingEvent } = item;
  let title = item.title;

  const shouldShowTaxAdvisor = useShouldShowTaxAdvisor();

  // Temporary fix to change the support menu title for users in the experiment
  if (shouldShowTaxAdvisor && title === 'sidebar.support.ai.title') {
    title = 'sidebar.support.ai_and_support.title';
  }

  const { t } = useTranslation();

  const { pathname } = useLocation();

  const navigate = useNavigate();

  const subscriptionPlan = useSubscriptionPlan();

  const { locks = {} } = subscriptionPlan?.features || {};

  const isActive =
    active ||
    to === pathname ||
    Boolean(children?.some((child) => matchPath(child, pathname)));

  const isLocked =
    isWeb &&
    item.lockKey &&
    locks?.[item.lockKey as keyof typeof locks] === true;

  return (
    <Tooltip content={item.tooltip ? t(item.tooltip) : null} placement="right">
      <div className="w-full group">
        <button
          onClick={() => {
            onClick?.();

            if (!to) return;

            // dont re-navigate to the current route
            // makes things easier when dealing with forms with id as search param
            // as it will trigger the router to re-navigate which will trigger an alert if the navigation is blocked
            if (to === pathname) return;

            if (tracingEvent) {
              logAmplitude(tracingEvent);
            }

            navigate(to);
          }}
          type="button"
          className={classNames(
            'flex flex-1 transition-all items-center text-left relative px-2 py-1 w-full rounded-lg',
            isActive
              ? (SidebarNavigationItemSelectedBGStyles as any)[type]
              : SidebarNavigationItemBGStyles[type] || 'bg-transparent',
            isLocked && 'opacity-50',
            className,
          )}
        >
          <div className="relative mr-2 flex items-center justify-center">
            <Icon
              className={classNames(
                'h-8 text-primary-600 transition-all duration-300',
                isActive
                  ? SideBarNavigationItemIconStyles[type]
                  : (SidebarNavigationItemDefaultTextStyles as any)[type],
              )}
            />
          </div>

          <span
            className={classNames(
              'text-base text-primary text-left font-semibold',
              isActive
                ? SidebarNavigationItemTextStyles[type]
                : (SidebarNavigationItemDefaultTextStyles as any)[type] ||
                    'transition-colors',
              titleContainerClassName,
            )}
          >
            {renderTitle?.() || t(title)}
          </span>

          {isLocked && (
            <LockClosedIcon className="w-4 h-4 text-primary-500 ml-2" />
          )}

          {item.BadgeComponent ? (
            <span className={classNames(item.BadgeComponentClassName)}>
              <item.BadgeComponent isActive={isActive} />
            </span>
          ) : null}
        </button>
      </div>
    </Tooltip>
  );
};

export default NavigationItem;
